import { onNodeDrag } from '@ui/lib/reactFlow/DragAndDrop/onNodeDrag'
import { onNodeDragStop } from '@ui/lib/reactFlow/DragAndDrop/onNodeDragStop'
import { defaultEdgeOptions } from '@ui/lib/reactFlow/helpers'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Node,
  NodeOrigin,
  ProOptions,
  ReactFlowProvider,
} from 'reactflow'

import useLayout from '@ui/lib/hooks/useLayout'
import edgeTypes from '@ui/pages/pipelinesWorkflow/components/EdgeTypes'
import { PipelineDataProvider } from '@ui/providers/PipelineDataProvider'
import { useNodeAndEdgeStore } from '@ui/stores'

import 'reactflow/dist/base.css'

import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { NodeTypeEnum } from '@ui/interfaces'
import nodeTypes from '@ui/pages/pipelinesWorkflow/components/NodeTypes'
import { PipelineActionModal } from '@ui/pages/pipelinesWorkflow/components/PipelineActionModal'
import { LayoutManager } from '@ui/pages/pipelinesWorkflow/components/PipelineLayoutManager'
import { PipelineModifiedWrapper } from '@ui/pages/pipelinesWorkflow/components/PipelineModifiedWrapper'
import { useLayoutPanelManagerStore } from '@ui/stores/pipelinesWorkflow'

const proOptions: ProOptions = { account: 'paid-pro', hideAttribution: true }
const nodeOrigin: NodeOrigin = [0, 0]

function ReactFlowWrapper() {
  const { nodes, edges, onNodesChange, onEdgesChange, onNodesDelete, onConnect, setNodes, setEdges, setRefreshLayout } =
    useNodeAndEdgeStore(
      useShallow((state) => ({
        nodes: state.nodes,
        edges: state.edges,
        onNodesChange: state.onNodesChange,
        onEdgesChange: state.onEdgesChange,
        onNodesDelete: state.onNodesDelete,
        onConnect: state.onConnect,
        setNodes: state.setNodes,
        setEdges: state.setEdges,
        setRefreshLayout: state.setRefreshLayout,
      }))
    )

  // Logic:
  // If we get close to the left side of a node, we get added as a child
  // If we get close to the right side of a node, we get injected as a parent
  // If we get close to the top or bottom of a node, we get injected as a sibling
  const onNodeDragCallback = (_: React.MouseEvent, targetNode: Node) => {
    onNodeDrag(targetNode, nodes, edges, setNodes, setEdges)
  }

  const onNodeDragStopCallback = (_: React.MouseEvent, targetNode: Node) => {
    onNodeDragStop(targetNode, nodes, edges, setNodes, setEdges, setRefreshLayout)
  }

  useLayout()

  return (
    <>
      <PipelineModifiedWrapper>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onConnect={onConnect}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          proOptions={proOptions}
          onInit={() => ({})}
          fitView
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesDelete={onNodesDelete}
          onNodeDrag={onNodeDragCallback}
          onNodeDragStop={onNodeDragStopCallback}
          nodeOrigin={nodeOrigin}
          defaultEdgeOptions={defaultEdgeOptions}>
          <Background variant={BackgroundVariant.Dots} />
          <Controls />
          <LayoutManager />
        </ReactFlow>
      </PipelineModifiedWrapper>
    </>
  )
}

export const PipelineWorkflowV2 = () => {
  const nodeId = useParams().nodeId

  const { resetLayoutPanelManagerStore } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      resetLayoutPanelManagerStore: state.reset,
    }))
  )

  const { currentNodeId, setCurrentNodeId, nodes } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
      nodes: state.nodes,
    }))
  )

  const firehoseNode = useMemo(() => nodes.find((node) => node.type === NodeTypeEnum.FIREHOSE), [nodes])

  // If we don't have a nodeId, set it to the firehose node
  // This is to ensure that we always have a current node
  useEffect(() => {
    if (!nodeId && !currentNodeId && firehoseNode) {
      setCurrentNodeId(firehoseNode.id)
    }
  }, [currentNodeId, firehoseNode, nodeId, setCurrentNodeId])

  useEffect(() => {
    resetLayoutPanelManagerStore()
  }, [resetLayoutPanelManagerStore])

  return (
    <ReactFlowProvider>
      <PipelineDataProvider>
        <div className="relative size-full p-0">
          <ReactFlowWrapper />
          {/* 
        <MiniMap
          nodeColor={nodeColor}
          nodeStrokeColor={nodeStrokeColor}
          position="bottom-right"
          onClick={onClickClose}
        /> */}
          <PipelineActionModal />
        </div>
      </PipelineDataProvider>
    </ReactFlowProvider>
  )
}
